import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Bell, User, Search, AlertTriangle, CheckCircle, Shield, HelpCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useLoginContext } from '../context/LoginContext'
import { apiRequest } from '../utils/api'

const TRANSITION_DURATION = '0.3s'
const TRANSITION_EASING = 'ease'

const Header = styled.header`
  position: fixed;
  top: 0;
  left: ${props => props.isSidebarExpanded ? '270px' : 'calc(64px + 0.5rem)'};
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  height: 64px;
  backdrop-filter: blur(12px);
  transition: left ${TRANSITION_DURATION} ${TRANSITION_EASING};
  background: ${props => props.theme === 'dark' ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.95)'};
  border-bottom: 1px solid ${props => props.theme === 'dark' ? 'rgba(55, 65, 81, 1)' : 'rgba(229, 231, 235, 1)'};
  z-index: 40;

  @media (max-width: 768px) {
    left: 0;
    flex-wrap: wrap;
    height: auto;
    padding: 0.75rem 1.5rem;
    gap: 0.75rem;
  }
`

const SearchWrapper = styled.div`
  flex: 1;
  position: relative;
  max-width: ${props => props.isSidebarExpanded ? '32rem' : '48rem'};
  margin-right: 1.5rem;
  transition: max-width ${TRANSITION_DURATION} ${TRANSITION_EASING};

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    margin-right: 0;
  }
`

const SearchInputContainer = styled.div`
  position: relative;
`

const SearchInput = styled.input`
  width: 100%;
  height: 42px;
  padding: 0 2.75rem;
  border: 1px solid ${props => props.theme === 'dark' ? '#374151' : '#d1d5db'};
  border-radius: 9999px;
  background-color: ${props => props.theme === 'dark' ? 'rgba(55, 65, 81, 0.5)' : 'rgba(249, 250, 251, 0.8)'};
  color: ${props => props.theme === 'dark' ? '#e5e7eb' : '#1f2937'};
  font-size: 0.9375rem;
  outline: none;
  backdrop-filter: blur(12px);
  transition: all 0.2s ease-in-out;

  &:focus {
    background-color: ${props => props.theme === 'dark' ? 'rgba(55, 65, 81, 0.8)' : 'rgba(255, 255, 255, 0.95)'};
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  &::placeholder {
    color: ${props => props.theme === 'dark' ? '#6b7280' : '#9ca3af'};
  }
`

const SearchIcon = styled(Search)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme === 'dark' ? '#9ca3af' : '#6b7280'};
  width: 18px;
  height: 18px;
`

const SuggestionsDropdown = styled.div`
  position: absolute;
  top: 45px;
  left: 0;
  right: 0;
  background: ${props => props.theme === 'dark' ? '#111827' : '#ffffff'};
  border: 1px solid ${props => props.theme === 'dark' ? '#374151' : '#e5e7eb'};
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-height: 200px;
  overflow-y: auto;
  z-index: 50;
`

const SuggestionItem = styled.div`
  padding: 1rem;
  cursor: pointer;
  color: ${props => props.theme === 'dark' ? '#e5e7eb' : '#1f2937'};
  border-bottom: 1px solid ${props => props.theme === 'dark' ? '#374151' : '#e5e7eb'};
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background: ${props => props.theme === 'dark' ? '#374151' : '#f9fafb'};
  }

  &:last-child {
    border-bottom: none;
  }
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const ScansCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1.25rem;
  height: 42px;
  border-radius: 9999px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  background: ${props => props.theme === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)'};
  color: ${props => props.theme === 'dark' ? '#93c5fd' : '#1e40af'};
  border: 1px solid ${props => props.theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)'};
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${props => props.theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.15)'};
  }
`

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const IconButton = styled.button`
  position: relative;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme === 'dark' ? 'rgba(55, 65, 81, 0.5)' : 'rgba(249, 250, 251, 0.8)'};
  border: 1px solid ${props => props.theme === 'dark' ? '#374151' : '#d1d5db'};
  border-radius: 9999px;
  color: ${props => props.theme === 'dark' ? '#e5e7eb' : '#4b5563'};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  backdrop-filter: blur(12px);

  &:hover {
    background: ${props => props.theme === 'dark' ? 'rgba(55, 65, 81, 0.8)' : 'rgba(255, 255, 255, 0.95)'};
    border-color: #3b82f6;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`

const NotificationDot = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 8px;
  height: 8px;
  background-color: #ef4444;
  border-radius: 50%;
  border: 2px solid ${props => props.theme === 'dark' ? '#111827' : '#ffffff'};
`

const Dropdown = styled.div`
  position: absolute;
  top: 56px;
  right: 0;
  width: ${props => props.type === 'notifications' ? '400px' : '280px'};
  max-height: ${props => props.type === 'notifications' ? '500px' : 'auto'};
  background: ${props => props.theme === 'dark' ? '#111827' : '#ffffff'};
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid ${props => props.theme === 'dark' ? '#374151' : '#e5e7eb'};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0) scale(1)' : 'translateY(-8px) scale(0.96)'};
  transform-origin: top right;
  transition: all ${TRANSITION_DURATION} cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
  z-index: 50;
`

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem;
  border-bottom: 1px solid ${props => props.theme === 'dark' ? '#374151' : '#e5e7eb'};

  h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: ${props => props.theme === 'dark' ? '#e5e7eb' : '#1f2937'};
  }

  span {
    font-size: 0.875rem;
    color: #6b7280;
  }
`

const NotificationsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme === 'dark' ? '#4b5563 #1f2937' : '#cbd5e1 #f1f5f9'};

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${props => props.theme === 'dark' ? '#1f2937' : '#f1f5f9'};
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme === 'dark' ? '#4b5563' : '#cbd5e1'};
    border-radius: 3px;
  }
`

const NotificationItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1.25rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
  border-bottom: 1px solid ${props => props.theme === 'dark' ? '#374151' : '#e5e7eb'};
  background: ${props => props.unread ? (props.theme === 'dark' ? 'rgba(55, 65, 81, 0.5)' : 'rgba(249, 250, 251, 0.8)') : 'transparent'};

  &:hover {
    background: ${props => props.theme === 'dark' ? 'rgba(55, 65, 81, 0.8)' : 'rgba(249, 250, 251, 0.95)'};
  }

  &:last-child {
    border-bottom: none;
  }
`

const NotificationIcon = styled.div`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  background: ${props => {
    switch (props.type) {
      case 'error': return props.theme === 'dark' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)';
      case 'success': return props.theme === 'dark' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.1)';
      case 'warning': return props.theme === 'dark' ? 'rgba(234, 179, 8, 0.2)' : 'rgba(234, 179, 8, 0.1)';
      default: return props.theme === 'dark' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)';
    }
  }};

  svg {
    color: ${props => {
      switch (props.type) {
        case 'error': return '#ef4444';
        case 'success': return '#22c55e';
        case 'warning': return '#eab308';
        default: return '#3b82f6';
      }
    }};
  }
`

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;

  h4 {
    margin: 0 0 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    color: ${props => props.theme === 'dark' ? '#e5e7eb' : '#1f2937'};
  }

  p {
    margin: 0 0 0.5rem;
    font-size: 0.875rem;
    color: ${props => props.theme === 'dark' ? '#9ca3af' : '#6b7280'};
    line-height: 1.5;
  }

  time {
    font-size: 0.75rem;
    color: ${props => props.theme === 'dark' ? '#6b7280' : '#9ca3af'};
  }
`

const MenuList = styled.div`
  padding: 0.5rem;
`

const MenuItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  border-radius: 8px;
  text-align: left;
  font-size: 0.9375rem;
  color: ${props => props.danger
    ? (props.theme === 'dark' ? '#ef4444' : '#dc2626')
    : (props.theme === 'dark' ? '#e5e7eb' : '#1f2937')};
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background: ${props => props.theme === 'dark' ? 'rgba(55, 65, 81, 0.8)' : 'rgba(249, 250, 251, 0.95)'};
  }
`

function Navbar({ toggleTheme, theme, isSidebarExpanded }) {
  const navigate = useNavigate()
  const { user, logout } = useLoginContext()
  const userTimeZone = user?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone

  const [searchQuery, setSearchQuery] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [scanData, setScanData] = useState({ totalScansUsed: 0, maxScansAvailable: 0 })
  const [notifications, setNotifications] = useState([])

  const notificationsRef = useRef(null)
  const userMenuRef = useRef(null)

  // Format notification type for display
  const formatNotificationType = (type) => {
    // Map specific types to better display names
    const typeMap = {
      'team_member_added': 'Team Member Added',
      'team_member_removed': 'Team Member Removed',
      'scan_completion': 'Scan Completion', 
      'critical_vulnerability': 'Critical Vulnerability',
      'scan_started': 'Scan Started',
      'payment_success': 'Payment Success',
      'payment_failed': 'Payment Failed',
      'plan_expiring': 'Plan Expiring',
      'team_update': 'Team Update'
    };
    
    // Use mapped name or fallback to capitalized words
    return typeMap[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get notification type for styling
  const getNotificationType = (notificationType) => {
    switch(notificationType) {
      case 'critical_vulnerability':
      case 'payment_failed':
      case 'error':
        return 'error';
      case 'scan_completion':
      case 'payment_success':
      case 'success':
        return 'success';
      case 'scan_started':
      case 'plan_expiring':
        return 'warning';
      default:
        return 'info';
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch(type) {
      case 'error':
        return <AlertTriangle size={18} />;
      case 'success':
        return <CheckCircle size={18} />;
      case 'warning':
        return <AlertTriangle size={18} />;
      case 'team_member_added':
      case 'team_member_removed':
      case 'team_update':
        return <User size={18} />;
      case 'scan_started':
        return <Shield size={18} />;
      default:
        return <CheckCircle size={18} />;
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setShowNotifications(false)
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setIsUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    apiRequest('/api/auth/total-scans', { method: 'GET' })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(setScanData)
      .catch(console.error)
  }, [])

  useEffect(() => {
    apiRequest('/api/organization/notifications', { method: 'GET' })
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        const formatted = data
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .map(n => ({
            id: n._id,
            title: formatNotificationType(n.notificationType),
            message: n.message,
            time: new Intl.DateTimeFormat('en-US', {
              timeZone: userTimeZone,
              dateStyle: 'medium',
              timeStyle: 'short'
            }).format(new Date(n.createdAt)),
            unread: !n.isRead,
            type: getNotificationType(n.notificationType)
          }))
        setNotifications(formatted)
      })
      .catch(console.error)
  }, [userTimeZone])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        apiRequest(`/api/scanhistory/search/?query=${encodeURIComponent(searchQuery)}`, { method: 'GET' })
          .then(res => res.ok ? res.json() : Promise.reject())
          .then(data => setSuggestions(data.results || []))
          .catch(() => setSuggestions([]))
      } else {
        setSuggestions([])
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  const handleNotificationToggle = () => {
    setShowNotifications(v => !v)
    setIsUserMenuOpen(false)
    if (notifications.some(n => n.unread)) {
      apiRequest('/api/organization/notifications/read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      })
        .then(res => res.ok && setNotifications(ns => ns.map(n => ({ ...n, unread: false }))))
        .catch(console.error)
    }
  }

  const handleNotificationClick = (id) => {
    apiRequest('/api/organization/notifications/read', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notification_id: id })
    })
      .then(res => res.ok && setNotifications(ns => ns.map(n => n.id === id ? { ...n, unread: false } : n)))
      .catch(console.error)
  }

  const handleUserToggle = () => {
    setIsUserMenuOpen(o => !o)
    setShowNotifications(false)
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleNavigate = (path) => {
    navigate(path)
    setIsUserMenuOpen(false)
  }

  const handleHelpClick = () => {
    window.open('https://help.zerodayops.com', '_blank', 'noopener,noreferrer')
  }

  return (
    <Header theme={theme} isSidebarExpanded={isSidebarExpanded}>
      {/* Search Section */}
      <SearchWrapper isSidebarExpanded={isSidebarExpanded}>
        <SearchInputContainer>
          <SearchIcon theme={theme} />
          <SearchInput
            theme={theme}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search scan history…"
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
            onKeyDown={e => {
              if (e.key === 'Enter' && suggestions.length) {
                navigate(suggestions[0].redirect_url)
                setSearchQuery('')
                setSuggestions([])
              }
            }}
          />
        </SearchInputContainer>
        
        {/* Search Suggestions */}
        {isSearchFocused && suggestions.length > 0 && (
          <SuggestionsDropdown theme={theme}>
            {suggestions.map((s, i) => (
              <SuggestionItem
                key={i}
                theme={theme}
                onMouseDown={() => {
                  navigate(s.redirect_url)
                  setSearchQuery('')
                  setSuggestions([])
                }}
              >
                {s.type} scan for <strong>{s.target}</strong> (ID: {s.scanId})
              </SuggestionItem>
            ))}
          </SuggestionsDropdown>
        )}
      </SearchWrapper>

      {/* Header Actions */}
      <HeaderActions>
        {/* Scans Counter */}
        <ScansCounter 
          theme={theme}
          onClick={() => navigate('/settings')}
        >
          <Shield size={18} />
          {scanData.totalScansUsed} / {scanData.maxScansAvailable} Scans
        </ScansCounter>

        {/* Action Buttons */}
        <ActionButtons>
          {/* Help Button */}
          <IconButton
            theme={theme}
            onClick={handleHelpClick}
          >
            <HelpCircle size={20} />
          </IconButton>

          {/* Notifications */}
          <div ref={notificationsRef} style={{ position: 'relative' }}>
            <IconButton onClick={handleNotificationToggle} theme={theme}>
              <Bell size={20} />
              {notifications.some(n => n.unread) && <NotificationDot theme={theme} />}
            </IconButton>

            {/* Notifications Dropdown */}
            <Dropdown type="notifications" $isOpen={showNotifications} theme={theme}>
              <DropdownHeader theme={theme}>
                <h3>Notifications</h3>
                <span>
                  {notifications.filter(n => n.unread).length} unread
                </span>
              </DropdownHeader>
              
              <NotificationsList theme={theme}>
                {notifications.map(n => (
                  <NotificationItem
                    key={n.id}
                    theme={theme}
                    unread={n.unread}
                    onClick={() => handleNotificationClick(n.id)}
                  >
                    <NotificationIcon type={n.type} theme={theme}>
                      {getNotificationIcon(n.type)}
                    </NotificationIcon>
                    <NotificationContent theme={theme}>
                      <h4>{n.title}</h4>
                      <p>{n.message}</p>
                      <time>{n.time}</time>
                    </NotificationContent>
                  </NotificationItem>
                ))}
              </NotificationsList>
            </Dropdown>
          </div>

          {/* User Menu */}
          <div ref={userMenuRef} style={{ position: 'relative' }}>
            <IconButton onClick={handleUserToggle} theme={theme}>
              <User size={20} />
            </IconButton>

            {/* User Dropdown */}
            <Dropdown $isOpen={isUserMenuOpen} theme={theme}>
              <MenuList>
                <MenuItem onClick={() => handleNavigate('/profile')} theme={theme}>
                  Profile Settings
                </MenuItem>
                <MenuItem onClick={handleLogout} theme={theme} danger>
                  Sign Out
                </MenuItem>
              </MenuList>
            </Dropdown>
          </div>
        </ActionButtons>
      </HeaderActions>
    </Header>
  )
}

export default Navbar